import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { photoEntryFromApi } from "../lib/fileUrl";
import { bindStreamToVideo, formatCameraError, requestCameraStream } from "../lib/cameraStream";
import { Camera, Check, Loader, RotateCcw, Upload } from "lucide-react";
import { toast } from "sonner";
import { compressImageForUpload, formatUploadError } from "../lib/compressImage";
import MobileUploadSizeHint from "./MobileUploadSizeHint";
import { PARKING_PHOTO_ANGLES } from "../lib/parkingPhotos";

/**
 * Guided parking photo capture for host listings — camera, upload, or QR-synced mobile shots.
 */
export default function ParkingPhotoCapture({ value = {}, onChange, testid = "parking-photo-capture", qrToken }) {
  const [activeAngle, setActiveAngle] = useState(PARKING_PHOTO_ANGLES[0].id);
  const [busy, setBusy] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const streamRef = useRef(null);
  const startAbortRef = useRef(null);
  const blobRef = useRef(null);

  const captured = value || {};
  const doneCount = PARKING_PHOTO_ANGLES.filter((a) => captured[a.id]).length;

  const stopCamera = () => {
    startAbortRef.current?.abort();
    startAbortRef.current = null;
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOpen(false);
    setCameraStarting(false);
    setCameraReady(false);
    setCameraError(null);
    setPreviewUrl(null);
  };

  const attachStream = useCallback(async (video) => {
    const stream = streamRef.current;
    if (!video || !stream) return;
    setCameraReady(false);
    const ready = await bindStreamToVideo(video, stream);
    setCameraReady(ready);
    if (!ready) toast.error("Camera preview didn't start — try upload instead");
  }, []);

  const setVideoRef = useCallback(
    (node) => {
      videoRef.current = node;
      if (node && streamRef.current && cameraOpen) attachStream(node);
    },
    [attachStream, cameraOpen],
  );

  const startCamera = async () => {
    if (cameraStarting || cameraOpen) return;
    setCameraError(null);
    setCameraReady(false);
    setCameraStarting(true);
    startAbortRef.current?.abort();
    const abort = new AbortController();
    startAbortRef.current = abort;
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("Camera not supported");
      const stream = await requestCameraStream({ facingMode: "environment", signal: abort.signal });
      if (abort.signal.aborted) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      setCameraOpen(true);
      if (videoRef.current) await attachStream(videoRef.current);
    } catch (e) {
      if (e?.name !== "AbortError") {
        const msg = formatCameraError(e);
        setCameraError(msg);
        toast.error(msg);
      }
      setCameraOpen(false);
    } finally {
      if (startAbortRef.current === abort) startAbortRef.current = null;
      setCameraStarting(false);
    }
  };

  useEffect(() => () => stopCamera(), []);

  const uploadBlob = async (blob, filename) => {
    setBusy(true);
    try {
      const raw = blob instanceof File ? blob : new File([blob], filename || "photo.jpg", { type: blob.type || "image/jpeg" });
      const compressed = await compressImageForUpload(raw);
      const fd = new FormData();
      fd.append("file", compressed, compressed.name);
      let res;
      if (qrToken) {
        res = await axios.post(
          `${API}/inspections/qr-sessions/${qrToken}/upload?angle=${activeAngle}`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
      } else {
        res = await axios.post(`${API}/upload`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      const entry = photoEntryFromApi(res.data, activeAngle);
      onChange({ ...captured, [activeAngle]: entry });
      toast.success(`${PARKING_PHOTO_ANGLES.find((a) => a.id === activeAngle)?.label} saved`);
      stopCamera();
      const next = PARKING_PHOTO_ANGLES.find((a) => !captured[a.id] && a.id !== activeAngle);
      if (next) setActiveAngle(next.id);
    } catch (e) {
      toast.error(formatUploadError(e.response?.data?.detail) || e.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const captureFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) {
      toast.error("Camera is still starting — wait a moment and try again");
      return;
    }
    const scale = Math.min(1, 1920 / Math.max(vw, vh));
    canvas.width = Math.round(vw * scale);
    canvas.height = Math.round(vh * scale);
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (blob) {
        blobRef.current = blob;
        setPreviewUrl(URL.createObjectURL(blob));
      }
    }, "image/jpeg", 0.85);
  };

  const confirmCapture = () => {
    if (blobRef.current) uploadBlob(blobRef.current, `parking-${activeAngle}.jpg`);
  };

  const onFilePick = async (files) => {
    const file = files?.[0];
    if (!file) return;
    await uploadBlob(file, file.name);
    if (fileRef.current) fileRef.current.value = "";
  };

  const current = PARKING_PHOTO_ANGLES.find((a) => a.id === activeAngle);

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-[var(--zc-text-3)]">
          Parking photos · {doneCount}/{PARKING_PHOTO_ANGLES.length} slots (min 1)
        </div>
        <div className="h-1.5 flex-1 max-w-[120px] ml-3 bg-[var(--zc-bg-3)] rounded-full overflow-hidden">
          <div className="h-full bg-[var(--zc-accent)] transition-all" style={{ width: `${(doneCount / PARKING_PHOTO_ANGLES.length) * 100}%` }} />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {PARKING_PHOTO_ANGLES.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => { stopCamera(); setActiveAngle(a.id); }}
            className={"px-3 h-8 text-xs rounded-full border font-semibold transition " +
              (activeAngle === a.id ? "border-[var(--zc-accent)] bg-[var(--zc-accent-soft)] text-[var(--zc-accent)]" :
                captured[a.id] ? "border-[var(--zc-success)] text-[var(--zc-success)]" : "border-[var(--zc-border)] text-[var(--zc-text-3)]")}
            data-testid={`${testid}-slot-${a.id}`}
          >
            {captured[a.id] ? <Check size={10} className="inline mr-1" /> : null}
            {a.label}
          </button>
        ))}
      </div>

      <div className="zc-card p-4">
        <div className="font-semibold text-sm">{current?.label}</div>
        <div className="text-xs text-[var(--zc-text-3)] mt-1">{current?.hint}</div>

        {captured[activeAngle]?.url && !cameraOpen && (
          <div className="mt-3 relative">
            <img src={captured[activeAngle].url} alt="" className="w-full max-h-64 object-contain rounded-lg border border-[var(--zc-border)] bg-[var(--zc-bg-2)]" />
            <button
              type="button"
              onClick={() => onChange({ ...captured, [activeAngle]: undefined })}
              className="absolute top-2 right-2 text-xs px-2 py-1 bg-white rounded shadow flex items-center gap-1"
            >
              <RotateCcw size={10} /> Retake
            </button>
          </div>
        )}

        {cameraOpen && (
          <div className="mt-3">
            <div className="relative rounded-lg overflow-hidden bg-black min-h-[200px] max-h-64">
              <video ref={setVideoRef} autoPlay playsInline muted className="w-full h-full max-h-64 min-h-[200px] object-contain" />
              {!cameraReady && !previewUrl && (
                <div className="absolute inset-0 grid place-items-center bg-black/80 text-white px-4 text-center">
                  <div>
                    <Loader size={22} className="animate-spin mx-auto mb-2" />
                    <p className="text-sm font-semibold">Starting camera preview…</p>
                  </div>
                </div>
              )}
            </div>
            <canvas ref={canvasRef} className="hidden" />
            {previewUrl ? (
              <div className="mt-2">
                <img src={previewUrl} alt="" className="w-full max-h-48 object-contain rounded-lg border" />
                <div className="flex gap-2 mt-2">
                  <button type="button" onClick={confirmCapture} disabled={busy} className="flex-1 h-10 zc-btn-primary text-xs">
                    {busy ? <Loader size={12} className="animate-spin mx-auto" /> : "Use photo"}
                  </button>
                  <button type="button" onClick={() => { setPreviewUrl(null); blobRef.current = null; }} className="h-10 px-3 zc-btn-ghost text-xs">Retake</button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 mt-2">
                <button type="button" onClick={captureFrame} disabled={!cameraReady} className="flex-1 h-10 zc-btn-primary text-xs flex items-center justify-center gap-1">
                  <Camera size={12} /> Capture
                </button>
                <button type="button" onClick={stopCamera} className="h-10 px-3 zc-btn-ghost text-xs">Cancel</button>
              </div>
            )}
          </div>
        )}

        {cameraStarting && (
          <div className="mt-4 rounded-lg border border-[var(--zc-border)] bg-[var(--zc-bg-2)] p-8 flex flex-col items-center gap-3 text-center">
            <Loader size={24} className="animate-spin text-[var(--zc-accent)]" />
            <p className="text-sm font-semibold">Opening camera…</p>
            <p className="text-xs text-[var(--zc-text-3)] max-w-xs">
              If your browser asks, tap Allow. On some phones this can take up to 20 seconds.
            </p>
            <button type="button" onClick={stopCamera} className="mt-1 h-9 px-4 zc-btn-ghost text-xs">Cancel</button>
          </div>
        )}

        {!captured[activeAngle]?.url && !cameraOpen && !cameraStarting && (
          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <button type="button" onClick={startCamera} disabled={busy} className="flex-1 h-11 zc-btn-primary text-xs flex items-center justify-center gap-2">
              <Camera size={14} /> Use camera
            </button>
            <label className="flex-1 h-11 zc-btn-outline-secondary text-xs flex items-center justify-center gap-2 cursor-pointer">
              <Upload size={14} /> Upload photo
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFilePick(e.target.files)} disabled={busy} />
            </label>
          </div>
        )}
        {!captured[activeAngle]?.url && !cameraOpen && !cameraStarting && (
          <MobileUploadSizeHint className="mt-2" />
        )}
        {cameraError && !cameraOpen && !cameraStarting && (
          <p className="text-xs text-[var(--zc-warn)] mt-3">{cameraError}</p>
        )}
      </div>
    </div>
  );
}
